import { doc, getDoc, updateDoc, collection, addDoc, query, where, orderBy, getDocs, onSnapshot } from "firebase/firestore"
import { db } from "./config"

export const setMessageRead = async (msgId) => {
    try {
        // Reference to the specific user document
        const messagesRef = doc(db, "messages", msgId)
        const messageSnapshot = await getDoc(messagesRef)
        const messageData = messageSnapshot.data()

        await updateDoc(messagesRef, { ...messageData, state: "read" })
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const addMessage = async (messageData) => {
    try {
        const messagesRef = collection(db, "messages");
        const docRef = await addDoc(messagesRef, {
            ...messageData,
            timestamp: new Date(),
            state: "unread"
        });
        return { id: docRef.id, ...messageData };
    } catch (error) {
        console.error("Error adding message:", error);
        throw error;
    }
}

export const getMessages = async (chatId) => {
    try {
        const messagesRef = collection(db, "messages");
        const q = query(
            messagesRef,
            where("chatId", "==", chatId),
            orderBy("timestamp", "asc")
        );
        const querySnapshot = await getDocs(q);
        const messages = [];
        querySnapshot.forEach((doc) => {
            messages.push({ id: doc.id, ...doc.data() });
        });
        return messages;
    } catch (error) {
        console.error("Error getting messages:", error);
        return [];
    }
}

// Single Collection Support Chat Functions
export const createSupportChat = async (userData) => {
    try {
        console.log('Creating support chat for user:', userData);
        const supportChatsRef = collection(db, "supportChats");
        const chatData = {
            userId: userData.uid,
            userName: userData.displayName || userData.email,
            userEmail: userData.email,
            status: "active",
            createdAt: new Date(),
            lastMessageAt: new Date(),
            messages: [] // Array to store messages
        };
        
        const docRef = await addDoc(supportChatsRef, chatData);
        return { id: docRef.id, ...chatData };
    } catch (error) {
        console.error("Error creating support chat:", error);
        throw error;
    }
}

export const getSupportChat = async (userId) => {
    try {
        const supportChatsRef = collection(db, "supportChats");
        const q = query(
            supportChatsRef,
            where("userId", "==", userId)
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            // Sort by lastMessageAt in JavaScript instead of Firebase query
            const chats = [];
            querySnapshot.forEach((doc) => {
                chats.push({ id: doc.id, ...doc.data() });
            });
            
            // Sort by lastMessageAt descending to get most recent
            chats.sort((a, b) => {
                const aTime = a.lastMessageAt?.seconds || a.lastMessageAt?.getTime() || 0;
                const bTime = b.lastMessageAt?.seconds || b.lastMessageAt?.getTime() || 0;
                return bTime - aTime;
            });
            
            const mostRecentChat = chats[0];
            return mostRecentChat;
        }
        return null;
    } catch (error) {
        console.error("Error getting support chat:", error);
        return null;
    }
}

export const sendSupportMessage = async (chatId, messageData) => {
    try {
        const chatRef = doc(db, "supportChats", chatId);
        const chatSnapshot = await getDoc(chatRef);
        
        if (!chatSnapshot.exists()) {
            throw new Error("Chat not found");
        }
        
        const chatData = chatSnapshot.data();
        
        const newMessage = {
            id: Date.now().toString(), // Simple ID for message
            senderId: messageData.senderId,
            senderName: messageData.senderName,
            senderType: messageData.senderType, // "user" or "admin"
            message: messageData.message,
            timestamp: new Date(),
            isRead: false
        };
        
        // Add message to the messages array
        const updatedMessages = [...(chatData.messages || []), newMessage];
        
        // Update the chat document with new message and timestamp
        await updateDoc(chatRef, {
            messages: updatedMessages,
            lastMessageAt: new Date(),
            status: "active" // Ensure chat is active when new message is sent
        });
        
        return { id: chatId, ...newMessage };
    } catch (error) {
        console.error("Error sending support message:", error);
        throw error;
    }
}

export const getSupportMessages = async (chatId) => {
    try {
        const chatRef = doc(db, "supportChats", chatId);
        const chatSnapshot = await getDoc(chatRef);
        
        if (!chatSnapshot.exists()) {
            return [];
        }
        
        const chatData = chatSnapshot.data();
        const messages = chatData.messages || [];
        return messages;
    } catch (error) {
        console.error("Error getting support messages:", error);
        return [];
    }
}

export const listenToSupportMessages = (chatId, callback) => {
    try {
        console.log('=== SETTING UP LISTENER ===');
        console.log('Chat ID for listener:', chatId);
        const chatRef = doc(db, "supportChats", chatId);
        
        return onSnapshot(chatRef, (doc) => {
            console.log('=== LISTENER TRIGGERED ===');
            console.log('Chat ID:', chatId);
            console.log('Document exists:', doc.exists());
            
            if (doc.exists()) {
                const chatData = doc.data();
                console.log('Full chat data received:', chatData);
                const messages = chatData.messages || [];
                console.log('Messages array from listener:', messages);
                console.log('Messages count:', messages.length);
                console.log('Calling callback with messages...');
                callback(messages);
                console.log('Callback executed');
            } else {
                console.log('Chat document not found in listener');
                console.log('Calling callback with empty array...');
                callback([]);
                console.log('Callback executed with empty array');
            }
        }, (error) => {
            console.error('=== LISTENER ERROR ===');
            console.error('Error in listener:', error);
        });
    } catch (error) {
        console.error("Error setting up listener:", error);
        return null;
    }
}

export const getAllSupportChats = async () => {
    try {
        const supportChatsRef = collection(db, "supportChats");
        const q = query(
            supportChatsRef,
            orderBy("lastMessageAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const chats = [];
        querySnapshot.forEach((doc) => {
            chats.push({ id: doc.id, ...doc.data() });
        });
        return chats;
    } catch (error) {
        console.error("Error getting all support chats:", error);
        return [];
    }
}

export const updateChatStatus = async (chatId, status) => {
    try {
        const chatRef = doc(db, "supportChats", chatId);
        await updateDoc(chatRef, { status });
    } catch (error) {
        console.error("Error updating chat status:", error);
        throw error;
    }
}

// Get initial unread message count
export const getUnreadMessageCount = async (userId) => {
    try {
        console.log('Getting initial unread message count for user:', userId);
        const messagesRef = collection(db, "messages");
        
        // Get all messages first to see the structure
        const allMessagesQuery = query(messagesRef);
        const allMessagesSnapshot = await getDocs(allMessagesQuery);
        const allMessages = [];
        allMessagesSnapshot.forEach((doc) => {
            allMessages.push({ id: doc.id, ...doc.data() });
        });
        
        console.log('All messages in database:', allMessages.length);
        console.log('Sample message structure:', allMessages[0]);
        
        // Filter messages for this user
        const userMessages = allMessages.filter(msg => {
            const isForUser = msg.recipientId === userId || msg.receiverId === userId || msg.receiver === userId || msg.to === userId;
            const isUnread = msg.state === 'unread' || msg.state === undefined;
            // console.log(`Message ${msg.id}: recipient=${msg.recipientId || msg.receiverId || msg.receiver || msg.to}, state=${msg.state}, isForUser=${isForUser}, isUnread=${isUnread}`);
            return isForUser && isUnread;
        });
        
        console.log('Filtered unread messages for user:', userMessages.length);
        console.log('Unread messages:', userMessages);
        
        return userMessages;
    } catch (error) {
        console.error("Error getting unread message count:", error);
        return [];
    }
}

// Real-time unread message count listener
export const listenToUnreadMessageCount = (userId, callback) => {
    try {
        console.log('=== SETTING UP REAL-TIME MESSAGE COUNT LISTENER ===');
        console.log('User ID:', userId);
        
        const messagesRef = collection(db, "messages");
        
        // Use a single query that covers all possible recipient fields
        const unreadMessagesQuery = query(
            messagesRef,
            where("state", "==", "unread")
        );
        
        console.log('Setting up single listener for unread messages...');
        
        // Single listener for all unread messages
        const unsubscribe = onSnapshot(unreadMessagesQuery, (snapshot) => {
            try {
                console.log('=== UNREAD MESSAGES LISTENER TRIGGERED ===');
                console.log('Total unread messages in database:', snapshot.size);
                
                const allUnreadMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('All unread messages:', allUnreadMessages);
                
                // Filter messages for this specific user
                const userUnreadMessages = allUnreadMessages.filter(msg => {
                    const isForUser = msg.recipientId === userId || msg.receiverId === userId || msg.to === userId;
                    // console.log(`Message ${msg.id}: recipient=${msg.recipientId || msg.receiverId || msg.to}, isForUser=${isForUser}`);
                    return isForUser;
                });
                
                console.log('Unread messages for user:', userUnreadMessages.length);
                console.log('Calling callback with user unread messages...');
                
                // Only call callback if we have a valid result
                if (Array.isArray(userUnreadMessages)) {
                    callback(userUnreadMessages);
                } else {
                    console.error('Invalid userUnreadMessages:', userUnreadMessages);
                    callback([]);
                }
            } catch (error) {
                console.error('Error processing unread messages:', error);
                callback([]);
            }
        }, (error) => {
            console.error("Error in unread messages listener:", error);
            callback([]);
        });
        
        // Return function to unsubscribe
        return () => {
            console.log('=== CLEANING UP MESSAGE COUNT LISTENER ===');
            unsubscribe();
        };
        
    } catch (error) {
        console.error("Error setting up unread message listener:", error);
        return () => {};
    }
}

// Test function to add a message and see if listener works
export const testAddMessage = async (userId) => {
    try {
        console.log('=== TESTING MESSAGE ADDITION ===');
        const testMessage = {
            senderId: 'test-sender',
            recipientId: userId, // Use recipientId to match the query
            message: 'Test message for real-time listener - ' + new Date().toLocaleTimeString(),
            timestamp: new Date(),
            state: 'unread'
        };
        
        const result = await addMessage(testMessage);
        console.log('Test message added:', result);
        return result;
    } catch (error) {
        console.error('Error adding test message:', error);
        throw error;
    }
}

export const addSuppportEmail = async (emailData) => {
    try {
        const supportEmailsRef = collection(db, 'support_emails');
        const docRef = await addDoc(supportEmailsRef, {
            ...emailData,
            timestamp: new Date(),
        });
        return { id: docRef.id, ...emailData };
    } catch (error) {
        console.error('Error adding support email:', error);
        throw error;
    }     
}